// src/services/offersService.js
import { apiRequest } from "./apiService";
import { buildQueryString } from "./queryString";
import { uploadMultipart } from "./httpUpload";

const encId = (id) => encodeURIComponent(String(id == null ? "" : id).trim());

const toStr = (v) => String(v == null ? "" : v).trim();

const toNullableStr = (v) => {
  const s = toStr(v);
  return s || null;
};

const toUndefinedStr = (v) => {
  const s = toStr(v);
  return s || undefined;
};

const toBoolStrict = (v) => {
  if (v === true || v === false) return v;
  if (v == null) return v;

  const s = String(v).trim().toLowerCase();
  if (["true", "1", "yes", "y"].includes(s)) return true;
  if (["false", "0", "no", "n"].includes(s)) return false;

  return v;
};

const toNum = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const normalizeListOffersParams = (params = {}) => {
  const p = { ...(params || {}) };

  if (p.campaign_id != null && p.campaignId == null) {
    p.campaignId = p.campaign_id;
  }
  delete p.campaign_id;

  const cursor =
    p.cursor ??
    p.next_cursor ??
    p.nextCursor ??
    p.last_key ??
    p.lastKey ??
    null;

  if (cursor != null && toStr(cursor) !== "") {
    p.cursor = cursor;
  } else {
    delete p.cursor;
  }

  delete p.next_cursor;
  delete p.nextCursor;
  delete p.last_key;
  delete p.lastKey;

  if ("active" in p) {
    const active = toBoolStrict(p.active);
    if (active === true || active === false) {
      p.active = active;
    } else if (p.active == null || toStr(p.active) === "") {
      delete p.active;
    }
  }

  if (typeof p.q === "string") {
    p.q = p.q.trim();
    if (!p.q) delete p.q;
  }

  if (p.limit != null) {
    const limit = toNum(p.limit, undefined);
    if (Number.isFinite(limit) && limit > 0) {
      p.limit = limit;
    } else {
      delete p.limit;
    }
  }

  return p;
};

const normalizeOfferPayload = (payload = {}) => {
  const p = { ...(payload || {}) };

  if (p.campaign_id != null && p.campaignId == null) {
    p.campaignId = p.campaign_id;
  }
  delete p.campaign_id;

  if ("active" in p) {
    p.active = toBoolStrict(p.active);
  }

  const out = {
    campaignId:
      "campaignId" in p
        ? (toStr(p.campaignId) === "" ? "" : toUndefinedStr(p.campaignId))
        : undefined,
    title: "title" in p ? toUndefinedStr(p.title) : undefined,
    code: "code" in p ? toUndefinedStr(p.code) : undefined,
    discountType: "discountType" in p ? toUndefinedStr(p.discountType) : undefined,
    discountValue:
      "discountValue" in p && p.discountValue !== ""
        ? Number(p.discountValue)
        : undefined,
    landingUrl: "landingUrl" in p ? toUndefinedStr(p.landingUrl) : undefined,
    active: "active" in p ? p.active : undefined,
    currency: "currency" in p ? toUndefinedStr(p.currency) : undefined,
    budget:
      "budget" in p && p.budget !== ""
        ? Number(p.budget)
        : undefined,
    startDate: "startDate" in p ? toUndefinedStr(p.startDate) : undefined,
    endDate: "endDate" in p ? toUndefinedStr(p.endDate) : undefined,
  };

  Object.keys(out).forEach((k) => out[k] === undefined && delete out[k]);
  return out;
};

const normalizeOffer = (raw = {}) => {
  const offerId = raw.offerId || raw.id || "";

  return {
    ...raw,
    offerId,
    id: offerId,
    campaignId: raw.campaignId || null,
    title: raw.title || "",
    code: raw.code || "",
    discountType: raw.discountType || "",
    discountValue: toNum(raw.discountValue, 0),
    landingUrl: raw.landingUrl || "",
    active: Boolean(raw.active),
    createdAt: raw.createdAt || "",
    updatedAt: raw.updatedAt || null,
  };
};

const normalizeOfferListResponse = (raw = {}) => {
  const items = Array.isArray(raw?.items) ? raw.items.map(normalizeOffer) : [];
  const lastKey = raw?.lastKey || raw?.last_key || null;
  const nextCursor = raw?.nextCursor || raw?.next_cursor || null;

  return {
    ...raw,
    items,
    count: toNum(raw?.count, items.length),
    last_key: lastKey,
    lastKey,
    next_cursor: nextCursor,
    nextCursor,
  };
};

const normalizeTopInvite = (raw = null) => {
  if (!raw || typeof raw !== "object") return null;

  return {
    inviteId: raw.inviteId || raw.id || "",
    clicks: toNum(raw.clicks, 0),
    redemptions: toNum(raw.redemptions, 0),
    revenue: toNum(raw.revenue, 0),
  };
};

const normalizeOfferOverviewQuery = (params = {}) => {
  const p = { ...(params || {}) };

  if (typeof p.from === "string") p.from = p.from.trim();
  if (typeof p.to === "string") p.to = p.to.trim();

  if (!p.from) delete p.from;
  if (!p.to) delete p.to;

  return p;
};

const normalizeOfferOverview = (raw = {}) => {
  return {
    ...raw,
    offerId: raw.offerId || "",
    from: raw.from || null,
    to: raw.to || null,
    clicks: toNum(raw.clicks, 0),
    redemptions: toNum(raw.redemptions, 0),
    revenue: toNum(raw.revenue, 0),
    conversionRate: toNum(raw.conversionRate, 0),
    topInvite: normalizeTopInvite(raw.topInvite),
  };
};

export const createOffer = async (payload) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error("Valid payload is required to create an offer.");
  }

  const res = await apiRequest("/offers", "POST", normalizeOfferPayload(payload));
  return normalizeOffer(res || {});
};

export const listOffers = async (params = {}) => {
  const qs = buildQueryString(normalizeListOffersParams(params));
  const res = await apiRequest(`/offers${qs}`, "GET");
  return normalizeOfferListResponse(res || {});
};

export const getOffer = async (offerId) => {
  if (!toStr(offerId)) {
    throw new Error("offerId is required.");
  }

  const res = await apiRequest(`/offers/${encId(offerId)}`, "GET");
  return normalizeOffer(res || {});
};

export const patchOffer = async (offerId, payload) => {
  if (!toStr(offerId)) {
    throw new Error("offerId is required.");
  }
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error("Valid payload is required to patch offer.");
  }

  const res = await apiRequest(
    `/offers/${encId(offerId)}`,
    "PATCH",
    normalizeOfferPayload(payload)
  );

  // backend may return only mutation result like { ok, updatedAt }
  if (
    res &&
    typeof res === "object" &&
    ("offerId" in res || "id" in res || "title" in res || "code" in res)
  ) {
    return normalizeOffer(res);
  }

  return res || { ok: true };
};

/**
 * Keep only if backend actually supports DELETE /offers/{offerId}.
 * From the backend you showed, this endpoint does NOT currently exist.
 * Safer to remove or leave unused until backend adds it.
 */
export const deleteOffer = async (offerId) => {
  if (!toStr(offerId)) {
    throw new Error("offerId is required.");
  }
  return apiRequest(`/offers/${encId(offerId)}`, "DELETE");
};

/**
 * GET /offers/{offerId}/overview?from=&to=
 */
export const getOfferOverview = async (offerId, params = {}) => {
  if (!toStr(offerId)) {
    throw new Error("offerId is required.");
  }

  const qs = buildQueryString(normalizeOfferOverviewQuery(params));
  const res = await apiRequest(`/offers/${encId(offerId)}/overview${qs}`, "GET");
  return normalizeOfferOverview(res || {});
};

/**
 * CSV import (FormData) — cannot use apiRequest because it forces JSON.
 */
export const importRedemptionsCsv = async (offerId, file) => {
  if (!toStr(offerId)) {
    throw new Error("offerId is required.");
  }
  if (!file) {
    throw new Error("CSV file is required.");
  }

  const form = new FormData();
  form.append("file", file);

  return uploadMultipart(`/offers/${encId(offerId)}/redemptions/import`, form);
};